/* eslint-disable import/no-deprecated */
import { intl } from '@fullstory/intl';
import { classNames } from '@fullstory/util/fmt';
import React, { PropsWithChildren, ReactNode } from 'react';
import { Button } from '../../components/Button';
import { Text } from '../../components/Text';
import { Tooltip } from '../../components/Tooltip';
import { identifyComponent } from '../../util';
import { Box } from '../Box';
import css from './index.module.scss';

interface RestrictedComponentProps {
  // The tooltip text when the element is disabled
  tooltip?: string | ReactNode;
  // The tooltip text when the element is enabled
  enabledTooltip?: string | ReactNode;
  // The variant for the enabled tooltip. Default is 'light'.
  enabledTooltipVariant?: React.ComponentProps<typeof Tooltip>['variant'];
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  // The tooltip action, only shown when the element is disabled.
  tooltipAction?: TooltipActionProps;
  disabled?: boolean;
  testId?: string;
}

const defaultTooltip = intl.formatMessage({
  defaultMessage: 'Your account does not have permission to modify this value.',
  id: 'LO7MjK',
});
export const RestrictedComponent: React.FC<PropsWithChildren<RestrictedComponentProps>> = ({
  tooltip = defaultTooltip,
  enabledTooltip,
  enabledTooltipVariant = 'light',
  tooltipPlacement = 'top',
  tooltipAction,
  disabled,
  children,
  testId = 'restricted-component',
  ...rest
}) => (
  <Box
    data-component="RestrictedComponent"
    {...rest}
    {...identifyComponent('restricted-component', rest)}
    className={classNames(css.restrictedComponent, {
      [css.disabled]: !!disabled,
    })}
    testId={testId}
  >
    {(disabled && !!tooltip) || enabledTooltip ? (
      <Tooltip
        placement={tooltipPlacement}
        variant={enabledTooltipVariant}
        testId="restricted-component-tooltip"
        tooltipContent={
          <Box>
            <Text
              appearance="bodySmall"
              color={enabledTooltipVariant === 'dark' ? 'default-inverse' : undefined}
            >
              {disabled ? tooltip : enabledTooltip}
            </Text>
            {` `}
            {disabled && tooltipAction && <TooltipAction {...tooltipAction} />}
          </Box>
        }
      >
        {children}
      </Tooltip>
    ) : (
      children
    )}
  </Box>
);

interface TooltipActionProps {
  text: string;
  href?: string;
  onClick?: () => void;
}

const TooltipAction: React.FC<TooltipActionProps> = ({ text, href, onClick }) => {
  if (href) {
    return (
      <a title={text} href={href} onClick={onClick} target="_blank" rel="noreferrer">
        {text}
      </a>
    );
  }
  return (
    <Button data-component="TooltipAction" variant="minimal" size="x-small" onClick={onClick}>
      {text}
    </Button>
  );
};
